/**
 * TeamManagement.js — Invite-based Team Management
 * =================================================
 * Admin: send invites, edit users, delete users, revoke invites
 * All users: change own password
 */

import React, { useState, useEffect, useCallback } from "react";
import api from "../api/client";
import { useAuth } from "../components/AuthContext";

const C = {
  bg: "#0f1724", card: "#1a2332", border: "#2a3a4e",
  text: "#e2e8f0", muted: "#94a3b8", accent: "#3b82f6",
  green: "#22c55e", greenBg: "rgba(34,197,94,.12)",
  red: "#ef4444", redBg: "rgba(239,68,68,.12)",
  orange: "#f59e0b", orangeBg: "rgba(245,158,11,.12)",
  purple: "#a855f7", purpleBg: "rgba(168,85,247,.12)",
};

const Badge = ({ children, bg, color }) => (
  <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: bg, color, textTransform: "uppercase", letterSpacing: ".5px" }}>{children}</span>
);
const roleBadge = (role) => {
  if (role === "Admin") return <Badge bg={C.purpleBg} color={C.purple}>Admin</Badge>;
  if (role === "Manager") return <Badge bg={C.orangeBg} color={C.orange}>Manager</Badge>;
  return <Badge bg={C.greenBg} color={C.green}>Viewer</Badge>;
};
const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box" };
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: ".5px" };
const th = { padding: "10px 14px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".5px", borderBottom: `1px solid ${C.border}`, textAlign: "left" };
const td = { padding: "10px 14px", fontSize: 13, borderBottom: `1px solid ${C.border}22` };
const btnSm = (borderColor, textColor) => ({ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: `1px solid ${borderColor}`, color: textColor, background: "transparent", cursor: "pointer" });

const Modal = ({ open, onClose, title, children, width = 480 }) => {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.6)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, width, maxWidth: "95vw", maxHeight: "85vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.5)" }}>
        <div style={{ padding: "18px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 17, color: C.text, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: "20px 24px" }}>{children}</div>
      </div>
    </div>
  );
};

export default function TeamManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const isAdmin = currentUser?.is_superuser || currentUser?.is_staff;
  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  const loadData = useCallback(async () => {
    try {
      const [u, i] = await Promise.all([
        api.get("/auth/users/"),
        isAdmin ? api.get("/auth/invites/") : Promise.resolve({ data: [] }),
      ]);
      setUsers(u.data);
      setInvites(i.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Invite ──
  const [inv, setInv] = useState({ email: "", first_name: "", last_name: "", role: "Viewer" });
  const [invError, setInvError] = useState("");
  const [sending, setSending] = useState(false);

  const handleInvite = async () => {
    setSending(true); setInvError("");
    try {
      const res = await api.post("/auth/invite/", inv);
      setInviteOpen(false);
      setInv({ email: "", first_name: "", last_name: "", role: "Viewer" });
      setInviteResult(res.data);
      loadData();
    } catch (err) {
      setInvError(err.response?.data?.error || "Failed");
    } finally { setSending(false); }
  };

  // ── Revoke Invite ──
  const handleRevokeInvite = async (invite) => {
    if (!window.confirm(`Delete invite for ${invite.email}?`)) return;
    try { await api.delete(`/auth/invites/${invite.id}/`); showToast("Invite deleted"); loadData(); }
    catch (err) { showToast("Failed to delete invite", "error"); }
  };

  // ── Edit User ──
  const [editData, setEditData] = useState({});
  const [editError, setEditError] = useState("");
  const openEdit = (u) => { setEditUser(u); setEditData({ email: u.email, first_name: u.first_name, last_name: u.last_name, role: u.role }); setEditError(""); };
  const handleEdit = async () => {
    setEditError("");
    try { await api.patch(`/auth/users/${editUser.id}/update/`, editData); showToast(`Updated ${editUser.username}`); setEditUser(null); loadData(); }
    catch (err) { setEditError(err.response?.data?.error || "Failed"); }
  };

  // ── Delete User ──
  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.first_name || u.username}? This cannot be undone.`)) return;
    try { await api.delete(`/auth/users/${u.id}/delete/`); showToast(`${u.username} deleted`); loadData(); }
    catch (err) { showToast(err.response?.data?.error || "Failed", "error"); }
  };

  // ── Change My Password ──
  const [myOldPw, setMyOldPw] = useState(""); const [myNewPw, setMyNewPw] = useState(""); const [myConfirmPw, setMyConfirmPw] = useState(""); const [changePwError, setChangePwError] = useState("");
  const handleChangeMyPw = async () => {
    setChangePwError("");
    if (myNewPw !== myConfirmPw) { setChangePwError("Passwords do not match"); return; }
    try { await api.post("/auth/change-password/", { old_password: myOldPw, new_password: myNewPw }); showToast("Password updated"); setChangePwOpen(false); setMyOldPw(""); setMyNewPw(""); setMyConfirmPw(""); }
    catch (err) { const d = err.response?.data?.error; setChangePwError(Array.isArray(d) ? d.join(", ") : (d || "Failed")); }
  };

  const pendingInvites = invites.filter(i => i.status === "Pending");

  return (
    <div style={{ padding: "0 24px 40px", color: C.text }}>
      {toast && <div style={{ position: "fixed", top: 20, right: 20, zIndex: 2000, padding: "12px 20px", borderRadius: 8, background: toast.type === "error" ? C.red : C.green, color: "#fff", fontSize: 14, fontWeight: 600, boxShadow: "0 8px 30px rgba(0,0,0,.4)" }}>{toast.msg}</div>}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>Team Management</h1>
          <p style={{ color: C.muted, fontSize: 14, margin: "4px 0 0" }}>Invite team members, manage roles and access</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setChangePwOpen(true)} style={{ padding: "8px 16px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Change My Password</button>
          {isAdmin && <button onClick={() => { setInviteOpen(true); setInvError(""); }} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: C.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>+ Invite Team Member</button>}
        </div>
      </div>

      {/* Pending Invites */}
      {isAdmin && pendingInvites.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "auto", marginBottom: 20 }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}`, fontSize: 14, fontWeight: 600, color: C.orange }}>Pending Invites ({pendingInvites.length})</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Name</th><th style={th}>Email</th><th style={th}>Role</th><th style={th}>Sent</th><th style={th}>Expires</th><th style={th}>Actions</th></tr></thead>
            <tbody>
              {pendingInvites.map(i => (
                <tr key={i.id}>
                  <td style={{ ...td, fontWeight: 600 }}>{i.first_name} {i.last_name}</td>
                  <td style={{ ...td, color: C.accent }}>{i.email}</td>
                  <td style={td}>{roleBadge(i.role)}</td>
                  <td style={{ ...td, fontSize: 12, color: C.muted }}>{i.created_at?.slice(0, 10)}</td>
                  <td style={{ ...td, fontSize: 12, color: C.muted }}>{i.expires_at?.slice(0, 10)}</td>
                  <td style={td}>
                    <button onClick={() => handleRevokeInvite(i)} style={btnSm(C.red, C.red)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Active Users */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "auto", marginBottom: 20 }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}`, fontSize: 14, fontWeight: 600 }}>Team Members ({users.length})</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={th}>Name</th><th style={th}>Username</th><th style={th}>Email</th><th style={th}>Role</th><th style={th}>Last Login</th>{isAdmin && <th style={th}>Actions</th>}</tr></thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ ...td, textAlign: "center", color: C.muted, padding: 30 }}>Loading...</td></tr>}
            {!loading && users.map(u => (
              <tr key={u.id}>
                <td style={{ ...td, fontWeight: 600 }}>{u.first_name} {u.last_name}</td>
                <td style={td}>{u.username}</td>
                <td style={{ ...td, color: C.accent }}>{u.email}</td>
                <td style={td}>{roleBadge(u.role)}</td>
                <td style={{ ...td, fontSize: 12, color: C.muted }}>{u.last_login ? u.last_login.slice(0, 10) : "Never"}</td>
                {isAdmin && <td style={td}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => openEdit(u)} style={btnSm(C.accent, C.accent)}>Edit</button>
                    {u.id !== currentUser?.id && <button onClick={() => handleDeleteUser(u)} style={btnSm(C.red, C.red)}>Delete</button>}
                  </div>
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Invite Modal ── */}
      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite Team Member">
        {invError && <div style={{ padding: "10px 14px", borderRadius: 6, background: C.redBg, color: C.red, fontSize: 13, marginBottom: 14 }}>{invError}</div>}
        <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>They'll receive an email with a link to create their own username and password.</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div><label style={labelStyle}>First Name</label><input style={inputStyle} value={inv.first_name} onChange={e => setInv(p => ({ ...p, first_name: e.target.value }))} placeholder="Jeremy" /></div>
          <div><label style={labelStyle}>Last Name</label><input style={inputStyle} value={inv.last_name} onChange={e => setInv(p => ({ ...p, last_name: e.target.value }))} placeholder="Weech" /></div>
        </div>
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>Email *</label><input style={inputStyle} type="email" value={inv.email} onChange={e => setInv(p => ({ ...p, email: e.target.value }))} placeholder="jeremy@helenapartners.com" /></div>
        <div style={{ marginBottom: 20 }}><label style={labelStyle}>Role</label>
          <select style={inputStyle} value={inv.role} onChange={e => setInv(p => ({ ...p, role: e.target.value }))}>
            <option value="Viewer">Viewer — read-only access</option>
            <option value="Manager">Manager — can edit data</option>
            <option value="Admin">Admin — full access + user management</option>
          </select>
        </div>
        <button onClick={handleInvite} disabled={sending} style={{ width: "100%", padding: "12px", borderRadius: 8, border: "none", background: sending ? C.muted : C.accent, color: "#fff", fontSize: 14, fontWeight: 600, cursor: sending ? "not-allowed" : "pointer" }}>
          {sending ? "Sending..." : "Send Invite"}
        </button>
      </Modal>

      {/* ── Invite Result ── */}
      <Modal open={!!inviteResult} onClose={() => setInviteResult(null)} title="Invite Sent" width={460}>
        <div style={{ textAlign: "center", padding: "10px 0 20px" }}>
          {inviteResult?.email_sent ? (
            <div style={{ padding: "12px 16px", borderRadius: 8, background: C.greenBg, border: `1px solid ${C.green}33`, color: C.green, fontSize: 14, marginBottom: 16 }}>Invitation emailed to {inviteResult.email}</div>
          ) : (
            <div style={{ padding: "12px 16px", borderRadius: 8, background: C.orangeBg, border: `1px solid ${C.orange}33`, color: C.orange, fontSize: 13, marginBottom: 12 }}>Email could not be sent automatically. Share the signup link manually:</div>
          )}
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px", marginBottom: 16, wordBreak: "break-all", fontSize: 13, color: C.accent, fontFamily: "monospace", userSelect: "all" }}>{inviteResult?.signup_link}</div>
          <button onClick={() => { navigator.clipboard.writeText(inviteResult?.signup_link || ""); showToast("Link copied"); }} style={{ padding: "8px 20px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, fontSize: 13, cursor: "pointer" }}>Copy Link</button>
        </div>
        <button onClick={() => setInviteResult(null)} style={{ width: "100%", padding: "12px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Done</button>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal open={!!editUser} onClose={() => setEditUser(null)} title={`Edit ${editUser?.username}`}>
        {editError && <div style={{ padding: "10px 14px", borderRadius: 6, background: C.redBg, color: C.red, fontSize: 13, marginBottom: 14 }}>{editError}</div>}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div><label style={labelStyle}>First Name</label><input style={inputStyle} value={editData.first_name || ""} onChange={e => setEditData(p => ({ ...p, first_name: e.target.value }))} /></div>
          <div><label style={labelStyle}>Last Name</label><input style={inputStyle} value={editData.last_name || ""} onChange={e => setEditData(p => ({ ...p, last_name: e.target.value }))} /></div>
        </div>
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>Email</label><input style={inputStyle} type="email" value={editData.email || ""} onChange={e => setEditData(p => ({ ...p, email: e.target.value }))} /></div>
        <div style={{ marginBottom: 20 }}><label style={labelStyle}>Role</label>
          <select style={inputStyle} value={editData.role || "Viewer"} onChange={e => setEditData(p => ({ ...p, role: e.target.value }))}>
            <option value="Viewer">Viewer</option><option value="Manager">Manager</option><option value="Admin">Admin</option>
          </select>
        </div>
        <button onClick={handleEdit} style={{ width: "100%", padding: "12px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Save Changes</button>
      </Modal>

      {/* ── Change Password Modal ── */}
      <Modal open={changePwOpen} onClose={() => { setChangePwOpen(false); setMyOldPw(""); setMyNewPw(""); setMyConfirmPw(""); setChangePwError(""); }} title="Change My Password">
        {changePwError && <div style={{ padding: "10px 14px", borderRadius: 6, background: C.redBg, color: C.red, fontSize: 13, marginBottom: 14 }}>{changePwError}</div>}
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>Current Password</label><input style={inputStyle} type="password" value={myOldPw} onChange={e => setMyOldPw(e.target.value)} /></div>
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>New Password</label><input style={inputStyle} type="password" value={myNewPw} onChange={e => setMyNewPw(e.target.value)} placeholder="Minimum 8 characters" /></div>
        <div style={{ marginBottom: 20 }}><label style={labelStyle}>Confirm New Password</label><input style={inputStyle} type="password" value={myConfirmPw} onChange={e => setMyConfirmPw(e.target.value)} /></div>
        <button onClick={handleChangeMyPw} style={{ width: "100%", padding: "12px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Update Password</button>
      </Modal>
    </div>
  );
}
